import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { Button, Container, Form, Row } from "react-bootstrap";
import {
  fetchLegacyModels,
  fetchMviewLegacyProjects,
} from "../../helpers/axios/projectsAxios";
import { useSelector } from "react-redux";
import {
  agGridOptionsNone,
  agGridTheme,
} from "../../helpers/constants/Constants";
import LoadingScreen from "../loadingScreen/LoadingScreen";

const formatDate = (params) =>
  params.value ? new Date(params.value).toLocaleString() : "";

const formatSize = (params) =>
  params.value != null ? `${(params.value / 1024 / 1024).toFixed(2)} MB` : "";

const AccLinkRenderer = (params) =>
  params.value ? (
    <a href={params.value} target="_blank" rel="noopener noreferrer">
      Open
    </a>
  ) : null;

const LegacyReports = () => {
  const { superUserDetails } = useSelector(
    (state) => state.globalReducer,
    );
    const [legacyProjects, setLegacyProjects] = useState([]);
    const [legacyProjectsData, setLegacyProjectsData] = useState([]);
  const [gridApi, setGridApi] = useState(null);
  const [gridPageSizeMode, setGridPageSizeMode] = useState("auto");
  const gridRef = useRef();


  const fetchLegacyData = useCallback(async () => {
    const legacyProjects = await fetchMviewLegacyProjects();
   setLegacyProjects(legacyProjects?.data || [])
      //loop through the legacy projects and build a promise all to fetch all the model data for each legacy project from ACC and concatenate them all into legacyProjectsData
      const legacyProjectsData = await Promise.all(
          legacyProjects?.data?.map(async (project) => {
              const modelData = await fetchLegacyModels(project.accId); 
              return { ...project, modelData };
            })
        );
      setLegacyProjectsData(legacyProjectsData || []);

  }, []);

  useEffect(() => {
    if (legacyProjects.length === 0) {
      fetchLegacyData();
    }
  }, [legacyProjects.length, fetchLegacyData]);

  const rowData = useMemo(
    () =>
      legacyProjectsData.flatMap((project) => {
        // modelData is an object ({ message }) when the ACC project has no models
        if (!Array.isArray(project.modelData) || project.modelData.length === 0) {
          return [
            {
              projectName: project.name,
              name: project.modelData?.message || "No models found",
              modelCount: 0,
            },
          ];
        }
        return project.modelData.map((model) => {
          const attrs = model.attributes || {};
          const extData = attrs.extension?.data || {};
          return {
            id: model.id,
            projectName: project.name,
            name: attrs.displayName || attrs.name,
            modelCount: 1,
            fileType: attrs.fileType,
            versionNumber: attrs.versionNumber,
            storageSize: attrs.storageSize,
            createUserName: attrs.createUserName,
            createTime: attrs.createTime,
            lastModifiedUserName: attrs.lastModifiedUserName,
            lastModifiedTime: attrs.lastModifiedTime,
            processState: extData.processState,
            extractionState: extData.extractionState,
            webView: model.links?.webView?.href,
          };
        });
      }),
    [legacyProjectsData],
  );

  const colDefs = useMemo(
    () => [
      { field: "projectName", headerName: "Project", rowGroup: true, hide: true },
      { field: "name", headerName: "Model", minWidth: 250 },
      {
        field: "modelCount",
        headerName: "Model Count",
        aggFunc: "sum",
        valueFormatter: (params) => (params.node?.group ? params.value : ""),
        maxWidth: 130,
        filter: false,
        sortable: false,
      },
      { field: "fileType", headerName: "Type", maxWidth: 100 },
      { field: "versionNumber", headerName: "Version", maxWidth: 110 },
      {
        field: "storageSize",
        headerName: "Size",
        valueFormatter: formatSize,
        filter: "agNumberColumnFilter",
        aggFunc: "sum",
      },
      { field: "createUserName", headerName: "Created By" },
      { field: "createTime", headerName: "Created", valueFormatter: formatDate },
      { field: "lastModifiedUserName", headerName: "Last Modified By" },
      {
        field: "lastModifiedTime",
        headerName: "Last Modified",
        valueFormatter: formatDate,
        sort: "desc",
      },
      { field: "processState", headerName: "Process State" },
      { field: "extractionState", headerName: "Extraction State" },
      {
        field: "webView",
        headerName: "ACC",
        cellRenderer: AccLinkRenderer,
        filter: false,
        sortable: false,
        maxWidth: 90,
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      filter: "agSetColumnFilter",
      suppressSizeToFit: false,
    }),
    [],
  );

  const autoGroupColumnDef = useMemo(
    () => ({
      headerName: "Project",
      minWidth: 350,
      filter: "agSetColumnFilter",
    }),
    [],
  );

  const fitGridColumns = useCallback(
    (apiInput) => {
      const api = apiInput || gridApi;
      if (!api) return;
      api.sizeColumnsToFit();
    },
    [gridApi],
  );

  const applyGridPageSizeMode = useCallback(
    (apiInput) => {
      const api = apiInput || gridApi;
      if (!api) return;

      // Include group rows so "All" fits every project header and child row
      const allRowCount = Math.max(
        (rowData?.length || 0) + (legacyProjectsData?.length || 0),
        1,
      );

      if (gridPageSizeMode === "all") {
        api.setGridOption("paginationAutoPageSize", false);
        api.setGridOption("paginationPageSize", allRowCount);
      } else if (gridPageSizeMode === "auto") {
        api.setGridOption("paginationAutoPageSize", true);
      } else {
        const pageSize = Number(gridPageSizeMode) || 25;
        api.setGridOption("paginationAutoPageSize", false);
        api.setGridOption("paginationPageSize", pageSize);
      }

      api.paginationGoToFirstPage();
    },
    [gridApi, gridPageSizeMode, rowData, legacyProjectsData],
  );

  useEffect(() => {
    applyGridPageSizeMode();
    fitGridColumns();
  }, [applyGridPageSizeMode, fitGridColumns]);

  const gridReady = (params) => {
    setGridApi(params.api);
    requestAnimationFrame(() => {
      fitGridColumns(params.api);
      applyGridPageSizeMode(params.api);
    });
  };

  return (
    <div className="project-config-page">
      <Container fluid className="project-config-shell">
        <h1 className="project-config-title">Legacy Reports</h1>
        {superUserDetails?.legacyReportAdmin ? (
          <Row className="project-config-grid-row">
            <div className="project-config-grid-wrap">
              <div className="project-config-grid-toolbar">
                <Button
                  className="project-config-action-btn mwh-btn-primary"
                  size="sm"
                  variant="outline-info"
                  onClick={() =>
                    gridApi?.exportDataAsExcel({
                      fileName: "Legacy Reports.xlsx",
                      exportedRows: "all",
                      rowGroupExpandState: "expanded",
                    })
                  }
                  disabled={!gridApi || rowData.length === 0}
                >
                  Export Excel
                </Button>
              </div>
              <div className="project-config-grid-table">
                {legacyProjectsData.length === 0 ? (
                  <LoadingScreen
                    input="Loading Legacy Projects"
                    width="12"
                    height="100%"
                    marginTop="6%"
                    variant="properties"
                    subtitle="Loading legacy project models..."
                    transparent={true}
                  />
                ) : (
                  <>
                    <AgGridReact
                      theme={agGridTheme}
                      rowData={rowData}
                      ref={gridRef}
                      columnDefs={colDefs}
                      defaultColDef={defaultColDef}
                      autoGroupColumnDef={autoGroupColumnDef}
                      groupDefaultExpanded={0}
                      suppressAggFuncInHeader={true}
                      paginationAutoPageSize={gridPageSizeMode === "auto"}
                      paginationPageSizeSelector={false}
                      onGridReady={gridReady}
                      onGridSizeChanged={(params) => {
                        fitGridColumns(params.api);
                        if (gridPageSizeMode === "auto") {
                          applyGridPageSizeMode(params.api);
                        }
                      }}
                      onFirstDataRendered={(params) => {
                        fitGridColumns(params.api);
                        applyGridPageSizeMode(params.api);
                      }}
                      gridOptions={agGridOptionsNone}
                    />
                    <div className="project-config-grid-rows-view">
                      <Form.Label className="mb-0 me-2">Page size</Form.Label>
                      <Form.Select
                        size="sm"
                        value={gridPageSizeMode}
                        onChange={(e) => {
                          setGridPageSizeMode(e.target.value);
                        }}
                        style={{ maxWidth: "180px" }}
                      >
                        <option value="auto">Auto</option>
                        <option value="25">25 per page</option>
                        <option value="50">50 per page</option>
                        <option value="100">100 per page</option>
                        <option value="all">All (scroll)</option>
                      </Form.Select>
                    </div>
                  </>
                )}
              </div>
            </div>
          </Row>
        ) : (
          <div className="alert alert-danger" role="alert">
            You do not have permission to view this page. Please contact your
            administrator.
          </div>
        )}
      </Container>
    </div>
  );
};

export default LegacyReports;
